import { createEffect, createMemo, createSignal, For, on, Show } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { History, RotateCcw } from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui";
import { WordDiff } from "@/components/ui/word-diff";
import { api } from "@/lib/api";
import { formatRelativeTime } from "@/features/activity/lib/activity-text";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { type components, getProjectVersionsQuery, queryKeys } from "@time-tracker/shared";
import type { ProjectVersionRecord } from "@time-tracker/powersync";

/** Sync window (last N per project in the local bucket). */
const LOCAL_SYNC_WINDOW = 10;
const DEEP_PAGE_SIZE = 20;

type ApiVersion = components["schemas"]["ProjectVersion"];

/** Maps a camelCase API row to the local (snake_case) shape. */
function fromApi(row: ApiVersion): ProjectVersionRecord {
  return {
    id: row.id,
    project_id: row.projectId,
    workspace_id: null,
    version_number: row.versionNumber,
    name: row.name,
    code: row.code ?? null,
    color: row.color ?? null,
    status: row.status ?? null,
    hourly_rate: row.hourlyRate ?? null,
    currency: row.currency ?? null,
    billable: row.billable === null || row.billable === undefined ? null : row.billable ? 1 : 0,
    text: row.text,
    client_ts: row.clientTs ?? null,
    server_ts: row.serverTs,
  };
}

/** Live billing definition of a project (for the diff against a version). */
export interface ProjectBillingCurrent {
  name: string | null;
  code: string | null;
  color: string | null;
  status: string | null;
  hourlyRate: number | null;
  currency: string | null;
  billable: number | boolean | null;
}

/**
 * Plain text of a billing definition — MUST mirror the server's `billing_text`
 * (packages/core-rust project_version.rs): name, then code/status/color (if
 * non-empty), then rate and billable. Internal serialization for the diff, not
 * UI copy, so its literals are not translated.
 */
function buildBillingText(def: {
  name: string | null;
  code: string | null;
  status: string | null;
  color: string | null;
  hourly_rate: number | null;
  currency: string | null;
  billable: number | boolean | null;
}): string {
  const parts: string[] = [def.name ?? ""];
  const code = (def.code ?? "").trim();
  if (code) {
    parts.push(`code: ${code}`);
  }
  const status = (def.status ?? "").trim();
  if (status) {
    parts.push(`status: ${status}`);
  }
  const color = (def.color ?? "").trim();
  if (color) {
    parts.push(`color: ${color}`);
  }
  if (def.hourly_rate !== null && def.hourly_rate !== undefined) {
    parts.push(`rate: ${def.hourly_rate} ${def.currency ?? "USD"}`);
  } else {
    parts.push("rate: —");
  }
  const billable = def.billable === 1 || def.billable === true || def.billable === null;
  parts.push(`billable: ${billable ? "yes" : "no"}`);
  return parts.join("\n");
}

interface ProjectVersionHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: () => string;
  current: () => ProjectBillingCurrent;
  onRestore: (version: ProjectVersionRecord) => void;
  restoring?: () => boolean;
}

export function ProjectVersionHistorySheet(props: ProjectVersionHistorySheetProps) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();

  const [selected, setSelected] = createSignal<ProjectVersionRecord | null>(null);
  const [view, setView] = createSignal<"changes" | "content">("changes");

  // Local window (PowerSync bucket): the last 10 versions offline.
  const localQuery = createPowerSyncQuery<ProjectVersionRecord>({
    queryKey: () => queryKeys.chronos.projectVersions(props.projectId()),
    db: () => db(),
    queryFn: () => {
      const dbi = drizzle();
      const id = props.projectId();
      return dbi && id ? getProjectVersionsQuery(dbi, id) : null;
    },
    enabled: () => Boolean(drizzle() && props.projectId()),
    placeholderData: keepPreviousData,
    suspense: false,
  });

  // Deep history (beyond the window) fetched via API on demand.
  const [deepVersions, setDeepVersions] = createSignal<ProjectVersionRecord[]>([]);
  const [deepCursor, setDeepCursor] = createSignal<string | null>(null);
  const [deepExhausted, setDeepExhausted] = createSignal(false);
  const [deepLoading, setDeepLoading] = createSignal(false);

  createEffect(
    on(
      () => props.open,
      (open) => {
        if (!open) {
          setSelected(null);
          setView("changes");
          setDeepVersions([]);
          setDeepCursor(null);
          setDeepExhausted(false);
        }
      },
      { defer: true },
    ),
  );

  const localVersions = () => localQuery.data ?? [];

  const versions = createMemo(() => {
    const seen = new Set<string>();
    const merged: ProjectVersionRecord[] = [];
    for (const v of [...localVersions(), ...deepVersions()]) {
      if (!seen.has(v.id)) {
        seen.add(v.id);
        merged.push(v);
      }
    }
    return merged.toSorted((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
  });

  const mayHaveDeeper = () => !deepExhausted() && localVersions().length >= LOCAL_SYNC_WINDOW;

  const currentText = createMemo(() => {
    const c = props.current();
    return buildBillingText({
      name: c.name,
      code: c.code,
      status: c.status,
      color: c.color,
      hourly_rate: c.hourlyRate,
      currency: c.currency,
      billable: c.billable,
    });
  });

  const loadOlder = async () => {
    if (deepLoading()) {
      return;
    }
    setDeepLoading(true);
    try {
      const oldest = versions().at(-1);
      const cursor = deepCursor() ?? oldest?.id;
      const { data } = await api.GET("/api/chronos/project/{id}/versions", {
        params: {
          path: { id: props.projectId() },
          query: { cursor, limit: DEEP_PAGE_SIZE },
        },
      });
      const rows = (data?.versions ?? []).map(fromApi);
      setDeepVersions((prev) => [...prev, ...rows]);
      if (data?.nextCursor) {
        setDeepCursor(data.nextCursor);
      } else {
        setDeepExhausted(true);
      }
    } catch {
      setDeepExhausted(true);
    } finally {
      setDeepLoading(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-lg">
      <div class="flex flex-col gap-3" data-testid="project-version-history">
        <div class="flex items-center gap-2">
          <History class="h-5 w-5 text-muted-foreground" />
          <h2 class="text-lg font-semibold">{t(msg`Rate history`)}</h2>
        </div>

        <Show
          when={selected()}
          fallback={
            <Show
              when={!localQuery.isLoading || versions().length > 0}
              fallback={<Skeleton class="h-40 w-full rounded-xl" />}
            >
              <Show
                when={versions().length > 0}
                fallback={
                  <EmptyState
                    icon={History}
                    title={t(msg`No versions yet`)}
                    description={t(
                      msg`Rate and billing changes to this project will appear here as versions.`,
                    )}
                  />
                }
              >
                <ul class="flex flex-col gap-1" data-testid="project-version-list">
                  <For each={versions()}>
                    {(version, index) => (
                      <li>
                        <button
                          type="button"
                          class="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-muted"
                          data-testid={`project-version-${index()}`}
                          onClick={() => {
                            setSelected(version);
                            setView("changes");
                          }}
                        >
                          <span class="font-medium">v{version.version_number}</span>
                          <span class="text-xs text-muted-foreground">
                            {formatRelativeTime(version.client_ts ?? version.server_ts, locale())}
                          </span>
                        </button>
                      </li>
                    )}
                  </For>
                </ul>
                <Show when={mayHaveDeeper()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="mt-1 w-full"
                    data-testid="project-version-load-more"
                    disabled={deepLoading()}
                    onClick={() => void loadOlder()}
                  >
                    {t(msg`Load older versions`)}
                  </Button>
                </Show>
              </Show>
            </Show>
          }
        >
          {(version) => (
            <div class="flex flex-col gap-3">
              <div class="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="project-version-back"
                  onClick={() => setSelected(null)}
                >
                  {t(msg`Back`)}
                </Button>
                <div class="flex gap-1">
                  <Button
                    variant={view() === "changes" ? "secondary" : "ghost"}
                    size="sm"
                    data-testid="project-version-view-changes"
                    onClick={() => setView("changes")}
                  >
                    {t(msg`Changes`)}
                  </Button>
                  <Button
                    variant={view() === "content" ? "secondary" : "ghost"}
                    size="sm"
                    data-testid="project-version-view-content"
                    onClick={() => setView("content")}
                  >
                    {t(msg`Full definition`)}
                  </Button>
                </div>
              </div>

              <div class="rounded-xl border border-border p-3 max-h-[45vh] overflow-y-auto">
                <Show
                  when={view() === "changes"}
                  fallback={
                    <p
                      class="whitespace-pre-wrap break-words text-sm leading-[1.75]"
                      data-testid="project-version-content"
                    >
                      {version().text}
                    </p>
                  }
                >
                  <WordDiff oldText={version().text ?? ""} newText={currentText()} />
                </Show>
              </div>

              <p class="text-xs text-muted-foreground">
                {t(
                  msg`Restoring never deletes history: the current definition is saved as a new version first, and existing time entries keep their sealed rate.`,
                )}
              </p>
              <Button
                class="gap-2"
                data-testid="restore-project-version"
                disabled={props.restoring?.()}
                onClick={() => props.onRestore(version())}
              >
                <RotateCcw class="h-4 w-4" />
                {t(msg`Restore this version`)}
              </Button>
            </div>
          )}
        </Show>
      </div>
    </Dialog>
  );
}
